import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Action, Store } from '@ngrx/store';
import { EMPTY, from, of } from 'rxjs';
import { catchError, filter, finalize, map, mergeMap, switchMap } from 'rxjs/operators';

import { getAllAutomaticPayments } from '@amfam/billing/auto-pay/data-access/lib/+state/auto-pay.actions';
import { selectAllBillAccounts } from '@amfam/billing/billaccount/data-access/lib/+state/billaccount.selectors';
import {
  getPaymentMethods,
  getPaymentMethodsSuccess
} from '@amfam/billing/payment-method/data-access/lib/+state/payment-method/payment-method.actions';
import { PaymentMethod } from '@amfam/billing/payment-method/util/lib/models/payment-method-util.models';
import { PaymentService } from '@amfam/billing/shared/util/lib/services/payment.service';
import { INVALID_IMPERSONATION_STATUS_CODE } from '@amfam/shared/models/lib/billing/billing-constants';
import {
  UNAVAILABLE_PAYMENTACCOUNT_ID,
  type PaymentAccount
} from '@amfam/shared/models/lib/billing/payment-account.model';
import { isTruthy } from '@amfam/shared/models/lib/util/type-helpers';
import { Applications } from '@amfam/shared/utility/shared-services/lib/application/application.enum';
import { ApplicationService } from '@amfam/shared/utility/shared-services/lib/application/application.service';
import { UtilService } from '@amfam/shared/utility/shared-services/lib/core/util.service';

import { PaymentaccountService } from '../services/paymentaccount.service';
import {
  PaymentAccountsAddFail as PaymentAccountActionsPaymentAccountsAddFail,
  PaymentAccountsLoadDetail as PaymentAccountActionsPaymentAccountsLoadDetail,
  PaymentAccountsLoadSuccess as PaymentAccountActionsPaymentAccountsLoadSuccess,
  PaymentAccountsAdd,
  PaymentAccountsAddFail,
  PaymentAccountsAddSuccess,
  PaymentAccountsCleanup,
  PaymentAccountScreenRecordingUpdate,
  PaymentAccountsDelete,
  PaymentAccountsDeleteFail,
  PaymentAccountsDeleteSuccess,
  PaymentAccountsEdit,
  PaymentAccountsEditFail,
  PaymentAccountsEditSuccess,
  PaymentAccountsLoad,
  PaymentAccountsLoadComplete,
  PaymentAccountsLoadDetail,
  PaymentAccountsLoadDetailSuccess,
  PaymentAccountsLoadFail,
  PaymentAccountsLoadSuccess,
  PaymentAccountsLoadUpdate,
  PaymentAccountsTruncate,
  PaymentAccountsUpdateModeAuth,
  PaymentAccountsUpdateModeAuthFail,
  PaymentAccountsUpdateModeAuthSuccess
} from './paymentaccount.actions';

export const load = createEffect(
  (action$ = inject(Actions), paymentService = inject(PaymentService), store = inject(Store)) => {
    return action$.pipe(
      ofType(PaymentAccountsLoad),
      switchMap(() =>
        paymentService.getPaymentMethods().pipe(
          finalize(() => {
            store.dispatch(PaymentAccountsLoadComplete());
          }),
          mergeMap(paymentMethodsResponse =>
            from(paymentMethodsResponse.paymentAccounts).pipe(
              map((fetchedPaymentAccount: PaymentMethod) =>
                PaymentAccountActionsPaymentAccountsLoadSuccess(fetchedPaymentAccount)
              ),
              mergeMap(successAction =>
                from([
                  successAction,
                  PaymentAccountsLoadUpdate({
                    paymentAccounts: paymentMethodsResponse.paymentAccounts
                  })
                ])
              )
            )
          ),
          catchError(error => of(PaymentAccountsLoadFail(error)))
        )
      )
    );
  },
  { functional: true }
);

export const loadAll = createEffect(
  (action$ = inject(Actions)) => {
    return action$.pipe(
      ofType(PaymentAccountsLoadSuccess),
      map(action => action.payload),
      mergeMap((pa: PaymentAccount) => [
        PaymentAccountActionsPaymentAccountsLoadDetail(pa.paymentAccountId)
      ]),
      catchError(error => of(PaymentAccountsLoadFail(error)))
    );
  },
  { functional: true }
);

export const loadAdminpayment = createEffect(
  (action$ = inject(Actions), applicationService = inject(ApplicationService)) => {
    return action$.pipe(
      ofType(PaymentAccountsLoadUpdate),
      map(action => action.payload),
      filter(() => applicationService.isApp(Applications.MYACCOUNT_ADMIN)), // Only proceed if app is admin
      map(pa =>
        getPaymentMethodsSuccess({
          paymentMethods: pa.paymentAccounts as PaymentMethod[],
          correlationId: pa.paymentAccountId
        })
      )
    );
  },
  { functional: true }
);

export const loadDetail = createEffect(
  (action$ = inject(Actions), paymentService = inject(PaymentService), store = inject(Store)) => {
    return action$.pipe(
      ofType(PaymentAccountsLoadDetail),
      map(action => action.payload),
      mergeMap(payload =>
        paymentService.getPaymentMethodDetail(payload).pipe(
          finalize(() => {
            store.dispatch(PaymentAccountsLoadComplete());
          }),
          mergeMap(paymentMethodsResponse => from([paymentMethodsResponse])),
          map(detailedPaymentAccount =>
            PaymentAccountsLoadDetailSuccess({
              creditDetails: detailedPaymentAccount.paymentAccounts?.[0]?.creditCard,
              nickName: detailedPaymentAccount.paymentAccounts?.[0]?.nickName,
              autopayRules: detailedPaymentAccount.autoPayRules,
              scheduledPayments: detailedPaymentAccount.payments
            })
          ),
          catchError(error => of(PaymentAccountsDeleteFail(error)))
        )
      ),
      catchError(error => of(PaymentAccountsDeleteFail(error)))
    );
  },
  { functional: true }
);

export const add = createEffect(
  (
    action$ = inject(Actions),
    paymentService = inject(PaymentService),
    utilService = inject(UtilService)
  ) => {
    return action$.pipe(
      ofType(PaymentAccountsAdd),
      map(action => action.payload),
      switchMap(payload => {
        return paymentService.addPaymentMethod(payload.customerPaymentPayload).pipe(
          mergeMap(res => {
            const returnArray: Action[] = [];
            /**
             * Status code of '202304' will be returned if the user is impersonating and is
             * trying to add a payment account without sufficient permission to do so.
             * In this case treat the response code of '202304' as a failure code and stop them from
             * moving forward.
             */
            if (res.status?.messages?.[0]?.code === INVALID_IMPERSONATION_STATUS_CODE) {
              returnArray.push(
                PaymentAccountActionsPaymentAccountsAddFail({
                  request: payload,
                  status: res.status
                })
              );
            } else {
              const successObject = {
                request: payload,
                response: res,
                confirmation: paymentService.buildAddEditPaymentMethodConfirmation(payload, res)
              };
              returnArray.push(
                PaymentAccountsAddSuccess(successObject),
                PaymentAccountsTruncate(UNAVAILABLE_PAYMENTACCOUNT_ID),
                PaymentAccountActionsPaymentAccountsLoadDetail(res.paymentAccountId)
              );

              returnArray.push(
                getPaymentMethods({
                  correlationId: utilService.generateId()
                })
              );
            }
            return returnArray;
          }),
          catchError(error => {
            return of(
              PaymentAccountActionsPaymentAccountsAddFail({ request: payload, status: error })
            );
          })
        );
      })
    );
  },
  { functional: true }
);

export const addFailCleanup = createEffect(
  (action$ = inject(Actions)) => {
    return action$.pipe(
      ofType(PaymentAccountsAddFail),
      map(action => action.payload),
      map(() => {
        return PaymentAccountsTruncate(UNAVAILABLE_PAYMENTACCOUNT_ID);
      })
    );
  },
  { functional: true }
);

export const editFailCleanup = createEffect(
  (action$ = inject(Actions)) => {
    return action$.pipe(
      ofType(PaymentAccountsEditFail),
      map(action => {
        return PaymentAccountsCleanup(action.payload.request.currentPaymentAccountNickName ?? '');
      })
    );
  },
  { functional: true }
);

export const updateFailCleanup = createEffect(
  (action$ = inject(Actions)) => {
    return action$.pipe(
      ofType(PaymentAccountsUpdateModeAuthFail),
      map(action => {
        return PaymentAccountsCleanup(action.payload.request.paymentAccount.nickName);
      })
    );
  },
  { functional: true }
);

export const deleteFailCleanup = createEffect(
  (action$ = inject(Actions)) => {
    return action$.pipe(
      ofType(PaymentAccountsDeleteFail),
      map(action => {
        return PaymentAccountsCleanup(action.payload.request.nickName);
      })
    );
  },
  { functional: true }
);

export const edit = createEffect(
  (
    action$ = inject(Actions),
    paymentService = inject(PaymentService),
    utilService = inject(UtilService)
  ) => {
    return action$.pipe(
      ofType(PaymentAccountsEdit),
      map(action => action.payload),
      switchMap(payload => {
        return paymentService
          .editPaymentMethod(payload.customerPaymentPayload, payload.paymentAccountId)
          .pipe(
            mergeMap(res => {
              const returnArray: Action[] = [];
              /**
               * Status code of '202304' will be returned if the user is impersonating and is
               * trying to edit a payment account without sufficient permission to do so.
               * In this case treat the response code of '202304' as a failure code and stop them from
               * moving forward.
               */
              if (res.status?.messages?.[0]?.code === INVALID_IMPERSONATION_STATUS_CODE) {
                returnArray.push(
                  PaymentAccountsEditFail({
                    request: payload,
                    status: res.status
                  })
                );
              } else {
                const paymentConfirmationObj = paymentService.buildAddEditPaymentMethodConfirmation(
                  payload,
                  res
                );
                const successObject = {
                  request: payload,
                  response: res,
                  confirmation: paymentConfirmationObj
                };
                returnArray.push(
                  PaymentAccountsEditSuccess(successObject),
                  PaymentAccountActionsPaymentAccountsLoadDetail(res.paymentAccountId),
                  PaymentAccountsCleanup(payload.finAcctPayload?.nickName ?? '')
                );
                returnArray.push(
                  getPaymentMethods({
                    correlationId: utilService.generateId()
                  })
                );
              }
              return returnArray;
            }),
            catchError(error => {
              return of(PaymentAccountsEditFail({ request: payload, status: error }));
            })
          );
      })
    );
  },
  { functional: true }
);

export const update = createEffect(
  (
    action$ = inject(Actions),
    paymentService = inject(PaymentService),
    utilService = inject(UtilService)
  ) => {
    return action$.pipe(
      ofType(PaymentAccountsUpdateModeAuth),
      map(action => action.payload),
      switchMap(payload => {
        return paymentService.updateModeOfAuth(payload.request, payload.paymentAccountId).pipe(
          mergeMap(res => {
            const returnArray: Action[] = [];
            const successObject = { request: payload.request, response: res };
            returnArray.push(
              PaymentAccountsUpdateModeAuthSuccess(successObject),
              PaymentAccountActionsPaymentAccountsLoadDetail(res.paymentAccountId),
              PaymentAccountsCleanup(payload.request?.paymentAccount?.nickName ?? '')
            );
            returnArray.push(
              getPaymentMethods({
                correlationId: utilService.generateId()
              })
            );

            return [...returnArray, ...(payload.paymentAction ?? [])];
          }),
          catchError(error => {
            return of(
              PaymentAccountsUpdateModeAuthFail({
                request: payload.request,
                correlationId: payload.correlationId,
                status: error
              })
            );
          })
        );
      })
    );
  },
  { functional: true }
);

export const deletePaymentAccount = createEffect(
  (
    action$ = inject(Actions),
    paymentService = inject(PaymentService),
    utilService = inject(UtilService),
    store = inject(Store)
  ) => {
    return action$.pipe(
      ofType(PaymentAccountsDelete),
      map(action => action.payload),
      concatLatestFrom(() => store.select(selectAllBillAccounts)),
      switchMap(([payload, billAccounts]) => {
        return paymentService.deletePaymentMethod(payload).pipe(
          mergeMap(res => {
            const returnArray: Action[] = [];
            /**
             * Status code of '202304' will be returned if the user is impersonating and is
             * trying to delete a payment account without sufficient permission to do so.
             * In this case treat the response code of '202304' as a failure code and stop them from
             * moving forward.
             */
            if (res.status?.messages?.[0]?.code === INVALID_IMPERSONATION_STATUS_CODE) {
              returnArray.push(
                PaymentAccountsDeleteFail({
                  request: payload,
                  status: res.status
                })
              );
            } else {
              const paymentConfirmationObj =
                paymentService.buildDeletePaymentMethodConfirmationData(payload, res);
              returnArray.push(
                PaymentAccountsDeleteSuccess({
                  request: payload,
                  response: res,
                  confirmation: paymentConfirmationObj
                }),
                PaymentAccountsTruncate(payload.nickName ?? '')
              );

              returnArray.push(
                getPaymentMethods({
                  correlationId: utilService.generateId()
                })
              );
              returnArray.push(
                getAllAutomaticPayments({
                  correlationId: utilService.generateId(),
                  billAccountNumbers: billAccounts
                    .map(billAccount => billAccount?.billAccountNumber)
                    .filter(isTruthy)
                })
              );
            }
            return returnArray;
          }),
          catchError(error => {
            return of(PaymentAccountsDeleteFail({ request: payload, status: error }));
          })
        );
      })
    );
  },
  { functional: true }
);

export const updateScreenRecording = createEffect(
  (
    action$ = inject(Actions),
    paymentAccountService = inject(PaymentaccountService),
    applicationService = inject(ApplicationService)
  ) => {
    return action$.pipe(
      ofType(PaymentAccountScreenRecordingUpdate),
      filter(() => applicationService.isApp(Applications.MYACCOUNT_ADMIN)),
      map(action => action.payload),
      switchMap(payload =>
        paymentAccountService.updateScreenRecording(payload).pipe(
          catchError(err => {
            console.error('[updateScreenRecording] failed:', err);
            return EMPTY;
          })
        )
      )
    );
  },
  { dispatch: false, functional: true }
);
